import type { LoaderFunctionArgs } from '@remix-run/node';
import { json } from '@remix-run/node';

import invariant from 'tiny-invariant';
import type { GetDigitalMagazineProps } from '@modules/magazine/api';
import { getDigitalMagazine } from '@modules/magazine/api';
import { MagazineViewerModal } from '@modules/magazine/component/magazine-viewer/MagazineViewerModal';
import { ClientOnly } from 'remix-utils';
import { useLoaderData, useNavigate } from '@remix-run/react';
import { BASE_URL } from '@modules/magazine/configs/constants';

import {
    getCanonicalHreflang,
    createMetaTitle,
    mergeMeta,
    createMetaDescription,
} from '~/modules/seo';
import type { APHandle } from '~/modules/root';
import dayjs from 'dayjs';

interface DigitalMagazineMetaProps {
    title: string;
    description: string;
}

const getDigitalMagazineMetaData = (
    data: GetDigitalMagazineProps | undefined
): DigitalMagazineMetaProps | undefined => {
    if (data) {
        const { type, title, subtitle, month, year } = data;
        const formattedMonth = dayjs()
            .month(month - 1)
            .format('MMMM');

        switch (type) {
            case 'Curated Collection':
                return {
                    title: `Curated Collection ${subtitle} ${formattedMonth} ${year}`,
                    description: `Explore our curated collection ${subtitle} ${formattedMonth} ${year}. Discover a thoughtfully curated collection of the finest products & inspirations.`,
                };

            case 'ArchiPros Talk':
                return {
                    title: `ArchiPro's Talk ${subtitle} ${formattedMonth} ${year}`,
                    description: `Discover features from leading architects, designers and inspiring products. Explore ArchiPro's talk ${subtitle} ${formattedMonth} ${year} and find out more. `,
                };

            case 'Commercial Edit':
                return {
                    title: `The Commercial Edit ${subtitle} ${formattedMonth} ${year}`,
                    description: `Discover commercial products and innovations from the Commercial Edit ${subtitle} ${formattedMonth} ${year}. Find the best in business solutions today.`,
                };

            case 'Project Feature':
                if (title == 'Project of the Month') {
                    return {
                        title: `${formattedMonth} ${year} Project of the Month`,
                        description: `Discover ${formattedMonth}'s Project of the Month: Immerse in breathtaking architecture, find design inspiration, and ignite your creativity.`,
                    };
                }
                break;

            default:
                break;
        }
    }

    return undefined;
};

export const meta = mergeMeta<typeof loader>(
    () => {
        return [];
    },
    (props) => {
        const { data } = props;
        const metaProps = getDigitalMagazineMetaData(data);

        const title = createMetaTitle(props, [
            metaProps?.title,
            data?.pageTitle,
        ]);

        const description = createMetaDescription([metaProps?.description]);

        const preloadImageLinks = data?.iframeLink
            ? [
                  {
                      rel: 'preload',
                      href: data.iframeLink,
                      as: 'document',
                      tagName: 'link',
                  },
              ]
            : [];

        return [
            { title },
            { description },
            ...getCanonicalHreflang(props),
            ...preloadImageLinks,
        ];
    }
);

export const handle: APHandle = {
    isDialogRoute: true,
};

export type DigitalMagazineLoader = typeof loader;

export const loader = async ({
    context,
    request,
    params,
}: LoaderFunctionArgs) => {
    invariant(params.urlSegment, 'A magazine URL segment is required');

    const [magazine] = await Promise.all([
        getDigitalMagazine({
            ...context,
            URLSegment: params.urlSegment,
        }),
    ]);

    const url = new URL(request.url);

    const searchParams = url.searchParams;

    const searchYear = searchParams.get('year');
    const searchMonth = searchParams.get('month');
    const searchSort = searchParams.get('sort');

    const returnURL = `${BASE_URL}${searchYear ? `/${searchYear}` : ''}${
        searchMonth ? `/${searchMonth}` : ''
    }${searchSort ? `?sort=${searchSort}` : ''}`;

    const toggleHref = url.searchParams.has('page')
        ? `${BASE_URL}?page=${url.searchParams.get('page')}`
        : returnURL;

    return json({
        iframeLink: magazine.iframeLink,
        type: magazine.type,
        title: magazine.title,
        pageTitle: magazine.title,
        subtitle: magazine.subtitle ?? '',
        urlSegment: magazine.urlSegment,
        month: magazine.month,
        year: magazine.year,
        toggleHref,
    });
};

const DigitalMagazineViewerPage = () => {
    const { iframeLink, urlSegment, toggleHref } =
        useLoaderData<typeof loader>();

    const navigate = useNavigate();

    return (
        <>
            <ClientOnly>
                {() => (
                    <MagazineViewerModal
                        toggleModal={() =>
                            navigate(toggleHref, {
                                state: { scroll: false },
                            })
                        }
                        isOpen={true}
                        iframeLink={iframeLink}
                        urlSegment={urlSegment}
                    />
                )}
            </ClientOnly>
        </>
    );
};

export default DigitalMagazineViewerPage;
